import { remittanceServices } from '../../../mocks/services';

interface HeroProps {
  onCompare: () => void;
  onHowItWorks: () => void;
}

const COUNTRIES = [
  { flag: '🇺🇸', name: 'EE.UU.' },
  { flag: '🇪🇸', name: 'España' },
  { flag: '🇨🇱', name: 'Chile' },
  { flag: '🇨🇴', name: 'Colombia' },
  { flag: '🇦🇷', name: 'Argentina' },
  { flag: '🇵🇪', name: 'Perú' },
];

const PROVIDER_STYLES: Record<string, { bg: string; hover: string; border: string }> = {
  wise: { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-500', border: 'border-emerald-600' },
  remitly: { bg: 'bg-teal-600', hover: 'hover:bg-teal-500', border: 'border-teal-600' },
  westernunion: { bg: 'bg-slate-800', hover: 'hover:bg-slate-700', border: 'border-slate-800' },
};

export default function Hero({ onCompare }: HeroProps) {
  const mainProviders = remittanceServices.filter(s =>
    ['wise', 'remitly', 'westernunion'].includes(s.id)
  );

  return (
    <section className="relative bg-gradient-to-b from-slate-50 to-white pt-28 pb-16 overflow-hidden">
      {/* Subtle background shapes */}
      <div className="absolute top-0 left-0 right-0 h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-green-100/60 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-48 w-80 h-80 bg-emerald-50/80 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-teal-50/50 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Top badge */}
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-xs font-bold px-4 py-2 rounded-full mb-7 border border-green-200">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          Actualizado hoy · Comparación gratuita · Sin registro
        </div>

        {/* Main headline */}
        <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-tight mb-5 tracking-tight">
          La mejor forma de enviar
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500">
            dinero a Venezuela
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
          Compara <strong className="text-gray-700">Wise, Remitly y Western Union</strong> en segundos y
          encuentra la opción más barata para enviar dinero a Venezuela.
        </p>

        {/* 3 Provider CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          {mainProviders.map((s) => {
            const style = PROVIDER_STYLES[s.id];
            return (
              <a
                key={s.id}
                href={s.link}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`inline-flex items-center justify-center gap-2 ${style.bg} ${style.hover} text-white font-bold text-sm md:text-base px-6 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-fill" />
                </span>
                Enviar dinero con {s.name}
              </a>
            );
          })}
        </div>

        {/* Compare button (secondary) */}
        <button
          onClick={onCompare}
          className="inline-flex items-center gap-2 text-green-700 hover:text-green-600 font-semibold text-sm border-b-2 border-green-300 hover:border-green-500 pb-0.5 transition-all cursor-pointer mb-12"
        >
          <i className="ri-bar-chart-2-line" />
          Ver comparación completa
        </button>

        {/* Trust line */}
        <div className="flex flex-col items-center gap-4">
          <p className="text-gray-400 text-sm font-medium">
            Usado por venezolanos en EE.UU., España y Europa
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {COUNTRIES.map((c) => (
              <div
                key={c.name}
                className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5"
              >
                <span className="text-sm leading-none">{c.flag}</span>
                <span className="text-gray-500 text-xs font-medium">{c.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-14 grid grid-cols-3 max-w-md mx-auto gap-0 rounded-2xl overflow-hidden border border-gray-200 bg-white">
          <div className="text-center py-5 px-4 border-r border-gray-200">
            <div className="text-2xl font-extrabold text-gray-900">4+</div>
            <div className="text-gray-400 text-xs mt-1">Servicios</div>
          </div>
          <div className="text-center py-5 px-4 border-r border-gray-200">
            <div className="text-2xl font-extrabold text-green-600">100%</div>
            <div className="text-gray-400 text-xs mt-1">Gratuito</div>
          </div>
          <div className="text-center py-5 px-4">
            <div className="text-2xl font-extrabold text-gray-900">Diario</div>
            <div className="text-gray-400 text-xs mt-1">Actualización</div>
          </div>
        </div>
      </div>
    </section>
  );
}
